import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    MessageSquare,
    Clock,
    CheckCircle2,
    AlertTriangle,
    Loader2,
    ArrowLeft,
    Sparkles,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

/**
 * Admin → SMS history. Lists the most recent sms_log entries with stats.
 */
const CATEGORY_LABELS = {
    messages: "Nachricht",
    offers: "Preisvorschlag",
    tradein: "Inzahlungnahme",
    buyrequests: "Ankaufanfrage",
    workshop_reminder: "Termin-Erinnerung",
    test: "Test",
};

export default function AdminSmsHistoryPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({ sent: 0, mocked: 0, failed: 0, limit: 50 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState(""); // "" | category key
    const [onlyFailed, setOnlyFailed] = useState(false);
    const [testPhone, setTestPhone] = useState("");
    const [testing, setTesting] = useState(false);

    const sendTestSms = async () => {
        if (!testPhone.trim()) { toast.error("Bitte Handynummer eingeben (z. B. +49170…)"); return; }
        setTesting(true);
        try {
            const { data } = await api.post("/admin/sms/test", { phone: testPhone.trim() });
            toast.success(data.mocked
                ? `Test-SMS an ${data.to} protokolliert (MOCK — Twilio nicht konfiguriert)`
                : `Test-SMS an ${data.to} gesendet ✔ (Twilio SID: ${data.sid || "—"})`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test-SMS fehlgeschlagen");
        }
        setTesting(false);
    };

    useEffect(() => {
        if (!authLoading && user?.role !== "admin") {
            navigate("/");
        }
    }, [user, authLoading, navigate]);

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (filter) params.category = filter;
            if (onlyFailed) params.only_failed = true;
            const { data } = await api.get("/admin/sms/history", { params });
            setItems(data.items || []);
            setStats(data.stats);
        } catch (e) {
            toast.error(e.response?.data?.detail || "SMS-Verlauf nicht ladbar");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "admin") load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, filter, onlyFailed]);

    if (authLoading || user?.role !== "admin") {
        return (
            <div className="flex items-center justify-center min-h-[40vh]">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-sms-history-page">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <MessageSquare className="w-7 h-7 text-[#FFCC00]" />
                    <div>
                        <h1 className="font-display text-2xl font-bold">SMS-Verlauf</h1>
                        <p className="text-sm text-gray-600">
                            Versandte und mocked SMS-Benachrichtigungen — die letzten {stats.limit} Einträge.
                        </p>
                    </div>
                </div>
                <Link to="/admin/dealers" className="inline-flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-[#111827]">
                    <ArrowLeft className="w-4 h-4" /> Admin-Übersicht
                </Link>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4" data-testid="sms-stats">
                <StatCard label="Echt versandt" value={stats.sent} tone="green" />
                <StatCard label="Mock (kein Twilio)" value={stats.mocked} tone="gray" />
                <StatCard label="Fehlgeschlagen" value={stats.failed} tone="red" />
            </div>

            <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="sms-test-panel">
                <div className="text-xs uppercase tracking-wider font-bold text-gray-500 inline-flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-[#FFCC00]" /> Test-SMS senden:
                </div>
                <input
                    type="tel"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendTestSms()}
                    placeholder="+49 170 1234567"
                    className="text-sm rounded-sm border border-gray-300 px-2.5 py-1.5 w-56 focus:border-[#111827] focus:outline-none"
                    data-testid="sms-test-phone"
                />
                <Button onClick={sendTestSms} disabled={testing} size="sm" className="rounded-sm text-xs bg-[#111827] hover:bg-black text-white font-bold" data-testid="sms-test-send">
                    {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Jetzt senden"}
                </Button>
                <span className="text-[11px] text-gray-500">Prüft die Twilio-Live-Anbindung (Absender +1 908 386 5932). Der Versand erscheint anschließend im Verlauf.</span>
            </div>

            <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 flex flex-wrap items-center gap-3">
                <div className="text-xs uppercase tracking-wider font-bold text-gray-500">Filter:</div>
                {["", ...Object.keys(CATEGORY_LABELS)].map((k) => (
                    <button
                        key={k || "all"}
                        onClick={() => setFilter(k)}
                        className={`text-xs px-2 py-1 rounded-sm font-bold uppercase tracking-wider ${filter === k ? "bg-[#FFCC00] text-[#111827]" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                        data-testid={`filter-${k || "all"}`}
                    >
                        {k ? CATEGORY_LABELS[k] : "Alle"}
                    </button>
                ))}
                <label className="ml-auto inline-flex items-center gap-2 text-xs">
                    <input
                        type="checkbox"
                        checked={onlyFailed}
                        onChange={(e) => setOnlyFailed(e.target.checked)}
                        className="w-4 h-4 accent-red-600"
                        data-testid="only-failed-toggle"
                    />
                    nur fehlgeschlagene
                </label>
                <Button onClick={load} size="sm" variant="outline" className="rounded-sm text-xs" data-testid="sms-reload">
                    Neu laden
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-8 text-center" data-testid="sms-empty">
                    <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Keine SMS-Einträge im gewählten Filter.</p>
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                    <table className="w-full text-sm" data-testid="sms-table">
                        <thead className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-600">
                            <tr>
                                <th className="text-left px-4 py-3">Zeit</th>
                                <th className="text-left px-4 py-3">Kategorie</th>
                                <th className="text-left px-4 py-3">Empfänger</th>
                                <th className="text-left px-4 py-3">Telefon</th>
                                <th className="text-left px-4 py-3">Nachricht</th>
                                <th className="text-left px-4 py-3">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((s) => (
                                <tr key={s.id} className="border-b border-gray-100 last:border-b-0 hover:bg-[#FFFBEB]/40" data-testid="sms-row">
                                    <td className="px-4 py-3 font-mono-num text-[11px] text-gray-700">
                                        <Clock className="inline-block w-3 h-3 text-gray-400 mr-1" />
                                        {s.at ? new Date(s.at).toLocaleString("de-DE") : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {s.category ? (
                                            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 font-bold text-[10px]">
                                                {CATEGORY_LABELS[s.category] || s.category}
                                            </Badge>
                                        ) : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {s.recipient ? (
                                            <>
                                                <div className="font-bold text-[#111827]">{s.recipient.name}</div>
                                                <div className="text-[10px] text-gray-500 font-mono-num">{s.recipient.email}</div>
                                            </>
                                        ) : <span className="text-gray-400">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono-num">{s.to || "—"}</td>
                                    <td className="px-4 py-3 text-xs truncate max-w-[260px]" title={s.body_preview}>{s.body_preview}</td>
                                    <td className="px-4 py-3">
                                        {s.ok && !s.mocked && (
                                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100 font-bold text-[10px]">
                                                <CheckCircle2 className="inline-block w-3 h-3 mr-1" /> versandt
                                            </Badge>
                                        )}
                                        {s.mocked && (
                                            <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 font-bold text-[10px]" title="Twilio-Credentials nicht gesetzt">
                                                <Sparkles className="inline-block w-3 h-3 mr-1" /> mock
                                            </Badge>
                                        )}
                                        {!s.ok && (
                                            <Badge className="bg-red-100 text-red-800 hover:bg-red-100 font-bold text-[10px]" title={s.reason || ""}>
                                                <AlertTriangle className="inline-block w-3 h-3 mr-1" /> fehler
                                            </Badge>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


function StatCard({ label, value, tone }) {
    const tones = {
        green: "border-green-300 bg-green-50 text-green-900",
        gray: "border-gray-300 bg-gray-50 text-gray-800",
        red: "border-red-300 bg-red-50 text-red-900",
    };
    return (
        <div className={`border rounded-sm p-4 ${tones[tone]}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold">{label}</div>
            <div className="text-3xl font-black font-mono-num mt-1">{value}</div>
        </div>
    );
}
